import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorNotification } from '../../contexts/ErrorNotificationContext';
import './ErrorToaster.css';

interface ErrorToasterProps {
  maxVisibleErrors?: number;
}

const ErrorToaster: React.FC<ErrorToasterProps> = ({ maxVisibleErrors = 5 }) => {
  const { t } = useTranslation();
  const { errors, dismissError } = useErrorNotification();
  const [reportingError, setReportingError] = useState<string | null>(null);

  const handleReportError = async (errorId: string, message: string, backendErrorId?: string | number) => {
    setReportingError(errorId);
    try {
      const errorData = {
        error_message: message,
        error_type: 'ClientError',
        endpoint: window.location.pathname,
        http_method: 'CLIENT_ERROR',
        http_status_code: 500,
        stack_trace: new Error().stack,
        request_data: {
          url: window.location.href,
          timestamp: new Date().toISOString(),
        },
      };

      const response = await fetch('/errors/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth0_token') || ''}`,
        },
        body: JSON.stringify(errorData),
      });

      if (response.ok) {
        const result = await response.json();
        dismissError(errorId);
      } else {
        console.error('Failed to report error:', response.statusText);
      }
    } catch (err) {
      console.error('Error reporting failed:', err);
    } finally {
      setReportingError(null);
    }
  };

  const visibleErrors = errors.slice(0, maxVisibleErrors);

  if (visibleErrors.length === 0) {
    return null;
  }

  return (
    <div className="error-toaster-container">
      {visibleErrors.map((error) => (
        <div key={error.id} className="error-toast-item">
          <div className="error-toast-content">
            <div className="error-toast-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10" cy="10" r="9" fill="#DC2626" stroke="#991B1B" strokeWidth="2" />
                <path
                  d="M10 6V10M10 14H10.01"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="error-toast-message">
              <p className="error-toast-text">{error.message}</p>
              {error.error_id && (
                <p className="error-toast-reference">{t('Reference ID:')} {error.error_id}</p>
              )}
            </div>
          </div>

          <div className="error-toast-actions">
            <button
              className="error-toast-report-btn"
              onClick={() =>
                handleReportError(error.id, error.message, error.error_id)
              }
              disabled={reportingError === error.id}
              title={t('Help us fix this issue by reporting it')}
            >
              {reportingError === error.id ? (
                <span className="btn-spinner"></span>
              ) : (
                t('Report')
              )}
            </button>
            <button
              className="error-toast-close-btn"
              onClick={() => dismissError(error.id)}
              title={t('Dismiss error')}
              aria-label={t('Close')}
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ErrorToaster;
